import { useTranslation } from "react-i18next"
import { DateTime } from "luxon"

import { useBaseContext } from "contexts/use-base-context"

import { TicketType, TicketCardProps } from "types"

import { ReactComponent as AddEncircled } from "assets/add-encircled.svg"
import { ReactComponent as RemoveEncircled } from "assets/remove-encircled.svg"

const isTicketAvailable = (ticket: TicketType) => {
  const now = DateTime.now()
  const salesStart = ticket.sale_start_date
    ? DateTime.fromISO(ticket.sale_start_date)
    : null
  const salesEnd = ticket.sale_end_date
    ? DateTime.fromISO(ticket.sale_end_date)
    : null

  if (salesStart && now < salesStart) return false
  if (salesEnd && now > salesEnd) return false

  if (ticket.num_remaining !== null && ticket.num_remaining <= 0) return false
  if (ticket.num_per_order !== null && ticket.num_per_order <= 0) return false

  return true
}

const TicketCard = ({
  ticket,
  watchTickets,
  register,
  setValue,
}: TicketCardProps) => {
  const isAvailable = isTicketAvailable(ticket)
  const { t } = useTranslation()
  const { currencySymbol } = useBaseContext()
  const currentQuantity = watchTickets[ticket.id]?.quantity || 0

  const handleQuantityChange = (newQuantity: number | string) => {
    if (!isAvailable) return

    let maxQuantity = Infinity
    const quantityAsNumber =
      typeof newQuantity === "string" ? parseInt(newQuantity) || 0 : newQuantity

    if (ticket.num_per_order > 0) {
      maxQuantity = ticket.num_per_order
    }

    if (
      (ticket.num_remaining || ticket.num_remaining === 0) &&
      ticket.num_remaining < maxQuantity
    ) {
      maxQuantity = ticket.num_remaining
    }

    const validQuantity = Math.min(Math.max(0, quantityAsNumber), maxQuantity)
    setValue(`tickets.${ticket.id}.quantity` as any, validQuantity)
  }

  const isMaxQuantity = () => {
    let maxQuantity = Infinity

    if (ticket.num_per_order > 0) {
      maxQuantity = ticket.num_per_order
    }

    if (
      (ticket.num_remaining || ticket.num_remaining === 0) &&
      ticket.num_remaining < maxQuantity
    ) {
      maxQuantity = ticket.num_remaining
    }

    return currentQuantity >= maxQuantity
  }

  const getAvailabilityMessage = () => {
    if (!isAvailable) {
      const now = DateTime.now()
      const salesStart = ticket.sale_start_date
        ? DateTime.fromISO(ticket.sale_start_date)
        : null
      const salesEnd = ticket.sale_end_date
        ? DateTime.fromISO(ticket.sale_end_date)
        : null

      if (salesStart && now < salesStart) {
        return `${t("selection.sales_start")} ${salesStart.toLocaleString(
          DateTime.DATETIME_MED
        )}`
      }
      if (salesEnd && now > salesEnd) {
        return `${t("selection.sales_ended")}`
      }
    }
    return ticket.display_remaining_number
      ? ticket.num_remaining === null
        ? `${t("selection.unlimited")} ${t("selection.available_tickets")}`
        : `${ticket.num_remaining < 0 ? 0 : ticket.num_remaining} ${t(
            "selection.available_tickets"
          )}`
      : ""
  }

  // Format price from cents to currency unit
  const formatPrice = (priceInCents: number) => {
    return (priceInCents / 100).toFixed(2)
  }

  return (
    <div
      key={ticket.id}
      className={`mb-4 border border-progress_bar_background rounded-lg shadow-sm bg-white text-default_card_text ${
        !isAvailable ? "opacity-60" : ""
      }`}
      role="region"
      aria-label={`${ticket.name} ticket information`}
    >
      <div className="p-4 md:p-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex flex-col">
              <h3
                className="text-base font-semibold text-black md:text-xl"
                id={`ticket-title-${ticket.id}`}
                aria-label={t("ticket.title", "Ticket Title")}
              >
                {ticket.name}
              </h3>
              {ticket.show_price !== false && (
                <div
                  className="text-base font-bold md:text-xl"
                  id={`ticket-cost-${ticket.id}`}
                  aria-label={t("ticket.cost", "Ticket Cost")}
                >
                  {currencySymbol}
                  {formatPrice(ticket.price)}
                </div>
              )}
            </div>
            <p
              className="mt-2 text-sm text-gray-600 md:text-base"
              id={`ticket-description-${ticket.id}`}
              aria-label={t("ticket.description", "Ticket Description")}
            >
              {ticket.description}
            </p>
          </div>
          <div className="flex flex-col items-center ml-4 md:ml-8">
            {ticket.num_per_order > 0 && (
              <p
                className="mb-2 text-xs text-gray-500 md:text-sm"
                id={`max-per-order-${ticket.id}`}
                aria-label={t("ticket.max_per_order", "Max Per Order")}
              >
                {t("ticket.max_per_order", "Max Per Order")}:{" "}
                {ticket.num_per_order}
              </p>
            )}
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                type="button"
                className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center ${
                  !isAvailable ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => handleQuantityChange(currentQuantity - 1)}
                disabled={!isAvailable}
                aria-label={t(
                  "ticket.decrease_quantity",
                  "Decrease ticket quantity"
                )}
                aria-controls={`ticket-quantity-${ticket.id}`}
              >
                <RemoveEncircled
                  fill={
                    isAvailable
                      ? "var(--theme_accent)"
                      : "var(--theme_text_medium)"
                  }
                  className="rounded-full shadow-lg"
                />
              </button>
              <input
                type="number"
                {...register(`tickets.${ticket.id}.quantity` as any, {
                  valueAsNumber: true,
                })}
                className={`w-12 md:w-16 text-center border border-input_border rounded-md text-sm md:text-base text-black ${
                  !isAvailable ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                onChange={(e) => handleQuantityChange(e.target.value)}
                disabled={!isAvailable}
                id={`ticket-quantity-${ticket.id}`}
                aria-label={t("ticket.quantity", "Ticket quantity")}
                role="spinbutton"
                aria-valuemin={0}
                aria-valuemax={
                  ticket.num_per_order > 0 ? ticket.num_per_order : undefined
                }
                aria-valuenow={currentQuantity}
              />
              <button
                type="button"
                className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center ${
                  !isAvailable || isMaxQuantity()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={() => handleQuantityChange(currentQuantity + 1)}
                disabled={!isAvailable || isMaxQuantity()}
                aria-label={t(
                  "ticket.increase_quantity",
                  "Increase ticket quantity"
                )}
                aria-controls={`ticket-quantity-${ticket.id}`}
              >
                <AddEncircled
                  fill={
                    isAvailable && !isMaxQuantity()
                      ? "var(--theme_accent)"
                      : "var(--theme_text_medium)"
                  }
                  className="rounded-full shadow-lg"
                />
              </button>
            </div>
            <p
              className="mt-2 text-xs text-center text-gray-500 md:text-sm"
              id={`availability-message-${ticket.id}`}
              aria-label={t("ticket.amount_available", "Amount Available")}
            >
              {getAvailabilityMessage()}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export { TicketCard }
